import {
  EGGS,
  ENEMIES,
  ITEMS,
  UPGRADES,
  WEAPONS,
} from "./core/content";
import type {
  EggDefinition,
  EnemyDefinition,
  ItemDefinition,
  StatModifiers,
  UpgradeDefinition,
  WeaponDefinition,
} from "./core/types";
import { SUPPORTED_LOCALES, t, type Locale, type MessageKey } from "./i18n";

type ContentKind = "egg" | "weapon" | "item" | "upgrade" | "enemy";

export interface LocalizedContent {
  name: string;
  description: string;
}

const EGG_NAME_ROWS: Readonly<Record<string, readonly string[]>> = {
  "fresh-egg": ["Fresh Egg", "新鲜蛋", "フレッシュエッグ", "신선한 달걀", "Huevo Fresco", "Œuf Frais", "Frisches Ei", "Ovo Fresco", "بيضة طازجة", "ताज़ा अंडा", "Telur Segar", "Yai Bichi"],
  "hard-boiled": ["Hard-Boiled", "水煮硬蛋", "ハードボイルド", "완숙 달걀", "Huevo Duro", "Œuf Dur", "Hartgekochtes Ei", "Ovo Cozido", "بيضة مسلوقة", "उबला अंडा", "Telur Rebus", "Yai la Kuchemsha"],
  "runner-egg": ["Runner Egg", "飞毛腿蛋", "ランナーエッグ", "러너 에그", "Huevo Corredor", "Œuf Coureur", "Läufer-Ei", "Ovo Corredor", "بيضة العدّاء", "रनर अंडा", "Telur Pelari", "Yai Mkimbiaji"],
  "scramble-egg": ["Scramble Egg", "疾速炒蛋", "スクランブルエッグ", "스크램블 에그", "Huevo Revuelto", "Œuf Brouillé", "Rührei", "Ovo Mexido", "بيضة مخفوقة", "भुर्जी अंडा", "Telur Orak-arik", "Yai Lililokorogwa"],
  "sunny-egg": ["Sunny Egg", "太阳蛋", "サニーエッグ", "써니 에그", "Huevo Soleado", "Œuf Soleil", "Sonniges Ei", "Ovo Ensolarado", "بيضة مشمسة", "सनी अंडा", "Telur Matahari", "Yai la Jua"],
  "poached-egg": ["Poached Egg", "水波蛋", "ポーチドエッグ", "수란", "Huevo Escalfado", "Œuf Poché", "Pochiertes Ei", "Ovo Pochê", "بيضة مسلوقة برفق", "पोच्ड अंडा", "Telur Rebus Lembut", "Yai la Maji"],
  "double-yolk": ["Double Yolk", "双黄蛋", "ダブルヨーク", "쌍노른자", "Doble Yema", "Double Jaune", "Doppeldotter", "Gema Dupla", "صفاران", "दोहरी जर्दी", "Kuning Ganda", "Kiini Maradufu"],
  "magnet-egg": ["Magnet Egg", "磁力蛋", "マグネットエッグ", "자석 달걀", "Huevo Imán", "Œuf Aimant", "Magnet-Ei", "Ovo Magnético", "بيضة مغناطيسية", "चुंबक अंडा", "Telur Magnet", "Yai la Sumaku"],
  "giant-egg": ["Giant Egg", "巨蛋", "ジャイアントエッグ", "거대 달걀", "Huevo Gigante", "Œuf Géant", "Riesen-Ei", "Ovo Gigante", "بيضة عملاقة", "विशाल अंडा", "Telur Raksasa", "Yai Jitu"],
  "spicy-egg": ["Spicy Egg", "辣味蛋", "スパイシーエッグ", "매운 달걀", "Huevo Picante", "Œuf Épicé", "Scharfes Ei", "Ovo Picante", "بيضة حارة", "तीखा अंडा", "Telur Pedas", "Yai Kali"],
  "lucky-egg": ["Lucky Egg", "幸运蛋", "ラッキーエッグ", "행운의 달걀", "Huevo de la Suerte", "Œuf Chanceux", "Glücks-Ei", "Ovo Sortudo", "بيضة محظوظة", "भाग्यशाली अंडा", "Telur Keberuntungan", "Yai la Bahati"],
  "royal-egg": ["Royal Egg", "皇家蛋", "ロイヤルエッグ", "왕실 달걀", "Huevo Real", "Œuf Royal", "Königliches Ei", "Ovo Real", "البيضة الملكية", "शाही अंडा", "Telur Kerajaan", "Yai la Kifalme"],
};

const BOSS_NAME_ROWS: Readonly<Record<string, readonly string[]>> = {
  "jam-queen": ["Jam Queen", "果酱女王", "ジャムクイーン", "잼 여왕", "Reina de Mermelada", "Reine de la Confiture", "Marmeladenkönigin", "Rainha da Geleia", "ملكة المربى", "जैम रानी", "Ratu Selai", "Malkia wa Jamu"],
  "clockwork-chef": ["Clockwork Chef", "发条大厨", "クロックワークシェフ", "태엽 요리사", "Chef de Relojería", "Chef Mécanique", "Uhrwerk-Koch", "Chef Mecânico", "الطاهي الآلي", "घड़ीसाज़ शेफ", "Koki Mekanik", "Mpishi wa Saa"],
  "the-breakfast": ["The Breakfast", "终极早餐", "ザ・ブレックファスト", "더 브렉퍼스트", "El Desayuno", "Le Petit-Déjeuner", "Das Frühstück", "O Café da Manhã", "الإفطار", "द ब्रेकफास्ट", "Sang Sarapan", "Kifungua Kinywa"],
};

const ZH_NAMES: Readonly<Record<string, string>> = {
  "weapon:shell-pistol": "蛋壳手枪", "weapon:fork": "餐叉", "weapon:yolk-blaster": "蛋黄爆破枪", "weapon:whisk": "战斗打蛋器", "weapon:eggbeater": "电动打蛋器", "weapon:toast-launcher": "吐司发射器", "weapon:pepper-shaker": "胡椒摇壶", "weapon:frying-pan": "平底锅", "weapon:butter-knife": "黄油刀", "weapon:hot-sauce": "辣酱喷射器", "weapon:freezer-pop": "冰棒", "weapon:rolling-pin": "擀面杖", "weapon:coffee-sprayer": "咖啡喷枪", "weapon:soda-popper": "汽水爆裂枪", "weapon:magnet-coil": "磁力线圈", "weapon:egg-timer": "鸡蛋计时器", "weapon:rubber-spatula": "橡胶锅铲", "weapon:salt-cannon": "盐粒大炮", "weapon:blender-blade": "搅拌机刀片", "weapon:toaster-arc": "烤面包机电弧", "weapon:jam-jar": "果酱罐", "weapon:milk-wand": "牛奶魔杖", "weapon:breakfast-laser": "早餐激光", "weapon:golden-ladle": "黄金汤勺",
  "item:tiny-carton": "迷你蛋盒", "item:running-shoes": "跑鞋", "item:paper-plate": "纸盘", "item:hot-pepper": "辣椒", "item:alarm-clock": "闹钟", "item:long-straw": "长吸管", "item:bouncy-napkin": "弹力餐巾", "item:warm-mug": "温热马克杯", "item:small-spring": "小弹簧", "item:sturdy-cup": "坚固杯子", "item:sugar-cube": "方糖", "item:ketchup-packet": "番茄酱包", "item:oven-mitt": "隔热手套", "item:protein-toast": "蛋白吐司", "item:double-espresso": "双份浓缩", "item:sharp-grater": "锋利擦丝器", "item:roller-skate": "轮滑鞋", "item:vacuum-lid": "真空杯盖", "item:lucky-spoon": "幸运勺", "item:soup-thermos": "汤品保温杯", "item:compressed-air": "压缩空气", "item:lunch-box": "午餐盒", "item:energy-jam": "能量果酱", "item:rubber-sole": "橡胶鞋底", "item:cast-iron-lid": "铸铁锅盖", "item:giant-carton": "巨型蛋盒", "item:triple-shot": "三倍浓缩", "item:chef-knife": "厨师刀", "item:rocket-apron": "火箭围裙", "item:tablecloth-net": "桌布捕网", "item:four-leaf-toast": "四叶吐司", "item:slow-cooker": "慢炖锅", "item:pressure-nozzle": "高压喷嘴", "item:fortified-bowl": "强化餐碗", "item:breakfast-combo": "早餐组合", "item:greased-tray": "润滑托盘", "item:titanium-shell": "钛合金蛋壳", "item:midnight-coffee": "午夜咖啡", "item:dragon-sauce": "龙息辣酱", "item:jet-skates": "喷气轮滑鞋", "item:black-hole-cup": "黑洞杯", "item:loaded-dice": "灌铅骰子", "item:eternal-kettle": "永恒水壶", "item:rail-nozzle": "轨道喷嘴", "item:royal-platter": "皇家餐盘", "item:sunrise-special": "日出特餐", "item:miracle-butter": "奇迹黄油", "item:perfect-breakfast": "完美早餐",
  "upgrade:thicker-shell": "加厚蛋壳", "upgrade:quick-feet": "灵活脚步", "upgrade:hard-boiled": "坚硬水煮", "upgrade:spicy-yolk": "辛辣蛋黄", "upgrade:rapid-crack": "快速裂击", "upgrade:strong-throw": "强力投掷", "upgrade:wide-reach": "广域拾取", "upgrade:lucky-roll": "幸运翻滚", "upgrade:shell-mend": "蛋壳修补", "upgrade:battle-breakfast": "战斗早餐", "upgrade:morning-rush": "清晨冲刺", "upgrade:ceramic-coat": "陶瓷涂层", "upgrade:sharp-prongs": "锋利尖叉", "upgrade:steady-aim": "稳定矄准", "upgrade:spiced-flame": "香辣火焰", "upgrade:critical-crack": "致命裂纹", "upgrade:siphon-straw": "吸血吸管", "upgrade:long-reach": "延长射程", "upgrade:lucky-break": "幸运破壳", "upgrade:morning-harvest": "清晨收获", "upgrade:kitchen-engineer": "厨房工程师", "upgrade:firm-grip": "稳固握持",
  "enemy:crumb": "面包屑", "enemy:mite": "果酱螨", "enemy:scuttler": "赤红疾行怪", "enemy:brute": "紫色蛮怪", "enemy:roller": "面团滚怪", "enemy:stinger": "糖刺怪", "enemy:slime": "莓果史莱姆", "enemy:moth": "面粉飞蛾", "enemy:charger": "平底锅冲锋怪", "enemy:shellback": "甲壳背兽", "enemy:red-captain": "赤红队长", "enemy:void-cook": "虚空厨师", "enemy:iron-maw": "钢铁巨口", "enemy:jam-queen": "果酱女王", "enemy:clockwork-chef": "发条大厨", "enemy:the-breakfast": "终极早餐", "enemy:tree": "早餐树",
};

const ARCHETYPE_KEYS: Readonly<Record<string, MessageKey>> = {
  Balanced: "archetype.balanced", Tank: "archetype.tank", Speed: "archetype.speed", "Rapid fire": "archetype.rapid", "Glass cannon": "archetype.glass", Recovery: "archetype.recovery", Multishot: "archetype.multishot", Collector: "archetype.collector", Juggernaut: "archetype.juggernaut", Aggressor: "archetype.aggressor", Trickster: "archetype.trickster", Arsenal: "archetype.arsenal",
};

export function localizeEgg(locale: Locale, egg: EggDefinition): LocalizedContent & { archetype: string } {
  const modifiers: StatModifiers = { ...egg.modifiers };
  const details = modifierText(locale, modifiers);
  if (egg.bonusProjectiles) details.push(stat(locale, egg.bonusProjectiles, "stat.projectiles"));
  if (egg.weaponSlotLimit) details.push(`${egg.weaponSlotLimit} ${t(locale, "stat.weaponSlots")}`);
  return {
    name: localizedName(locale, "egg", egg.id, egg.name),
    archetype: t(locale, ARCHETYPE_KEYS[egg.archetype] ?? "archetype.balanced"),
    description: details.length > 0 ? details.join(" · ") : t(locale, "content.balanced"),
  };
}

export function localizeWeapon(locale: Locale, weapon: WeaponDefinition): LocalizedContent {
  if (locale === "en") return { name: weapon.name, description: weapon.description };
  const projectileCount = Math.max(1, weapon.projectileCount);
  const parts = [
    `${t(locale, "stat.baseDamage")} ${weapon.damage}`,
    `${t(locale, "stat.cooldown")} ${number(locale, weapon.intervalTicks / 60)}s`,
    `${projectileCount} ${t(locale, "stat.projectileCount")}`,
  ];
  if (weapon.pierces > 0) parts.push(`${t(locale, "stat.pierce")} ${weapon.pierces}`);
  return { name: localizedName(locale, "weapon", weapon.id, weapon.name), description: parts.join(" · ") };
}

export function localizeItem(locale: Locale, item: ItemDefinition): LocalizedContent {
  const localized = localizeModifiers(locale, "item", item.id, item.name, item.description, item.modifiers);
  const effect = item.effect ? itemEffectText(locale, item.effect) : "";
  return {
    ...localized,
    description: [localized.description, effect].filter(Boolean).join(" · "),
  };
}

export function localizeUpgrade(
  locale: Locale,
  upgrade: UpgradeDefinition,
  multiplier = 1,
): LocalizedContent {
  const scaledModifiers = Object.fromEntries(
    Object.entries(upgrade.modifiers).map(([key, value]) => [
      key,
      Math.round(value * multiplier * 100) / 100,
    ]),
  ) as StatModifiers;
  return {
    name: localizedName(locale, "upgrade", upgrade.id, upgrade.name),
    description: modifierText(locale, scaledModifiers).join(" · "),
  };
}

export function localizeEnemyName(locale: Locale, enemy: EnemyDefinition): string {
  return localizedName(locale, "enemy", enemy.id, enemy.name);
}

export function localizeKnownContentName(locale: Locale, englishName: string): string {
  const egg = EGGS.find((entry) => entry.name === englishName);
  if (egg) return localizeEgg(locale, egg).name;
  const weapon = WEAPONS.find((entry) => entry.name === englishName);
  if (weapon) return localizeWeapon(locale, weapon).name;
  const item = ITEMS.find((entry) => entry.name === englishName);
  if (item) return localizeItem(locale, item).name;
  const upgrade = UPGRADES.find((entry) => entry.name === englishName);
  if (upgrade) return localizeUpgrade(locale, upgrade).name;
  const enemy = ENEMIES.find((entry) => entry.name === englishName);
  return enemy ? localizeEnemyName(locale, enemy) : englishName;
}

export function hasCompleteChineseCatalog(): boolean {
  return [
    ...WEAPONS.map((entry) => `weapon:${entry.id}`),
    ...ITEMS.map((entry) => `item:${entry.id}`),
    ...UPGRADES.map((entry) => `upgrade:${entry.id}`),
    ...ENEMIES.map((entry) => `enemy:${entry.id}`),
  ].every((key) => ZH_NAMES[key] !== undefined);
}

function localizeModifiers(
  locale: Locale,
  kind: ContentKind,
  id: string,
  fallbackName: string,
  fallbackDescription: string,
  modifiers: StatModifiers,
): LocalizedContent {
  return {
    name: localizedName(locale, kind, id, fallbackName),
    description:
      locale === "en" ? fallbackDescription : modifierText(locale, modifiers).join(" · "),
  };
}

function localizedName(locale: Locale, kind: ContentKind, id: string, fallback: string): string {
  if (locale === "zh-CN") return ZH_NAMES[`${kind}:${id}`] ?? EGG_NAME_ROWS[id]?.[1] ?? fallback;
  const row = kind === "egg" ? EGG_NAME_ROWS[id] : kind === "enemy" ? BOSS_NAME_ROWS[id] : undefined;
  const index = SUPPORTED_LOCALES.indexOf(locale);
  return row?.[index] ?? fallback;
}

function modifierText(locale: Locale, modifiers: StatModifiers): string[] {
  const result: string[] = [];
  if (modifiers.maxHealth) result.push(stat(locale, modifiers.maxHealth, "stat.maxHealth"));
  if (modifiers.speed) result.push(stat(locale, modifiers.speed, "stat.speed"));
  if (modifiers.armor) result.push(stat(locale, modifiers.armor, "stat.armor"));
  if (modifiers.damagePercent) result.push(stat(locale, modifiers.damagePercent, "stat.damage", true));
  if (modifiers.meleeDamage) result.push(stat(locale, modifiers.meleeDamage, "stat.meleeDamage"));
  if (modifiers.rangedDamage) result.push(stat(locale, modifiers.rangedDamage, "stat.rangedDamage"));
  if (modifiers.elementalDamage) result.push(stat(locale, modifiers.elementalDamage, "stat.elementalDamage"));
  if (modifiers.attackSpeedPercent) result.push(stat(locale, modifiers.attackSpeedPercent, "stat.attackSpeed", true));
  if (modifiers.critChancePercent) result.push(stat(locale, modifiers.critChancePercent, "stat.critChance", true));
  if (modifiers.lifeStealPercent) result.push(stat(locale, modifiers.lifeStealPercent, "stat.lifeSteal", true));
  if (modifiers.projectileSpeedPercent) result.push(stat(locale, modifiers.projectileSpeedPercent, "stat.projectileSpeed", true));
  if (modifiers.range) result.push(stat(locale, modifiers.range, "stat.range"));
  if (modifiers.pickupRadius) result.push(stat(locale, modifiers.pickupRadius, "stat.pickupRadius"));
  if (modifiers.dodgePercent) result.push(stat(locale, modifiers.dodgePercent, "stat.dodge", true));
  if (modifiers.regenerationPerSecond) result.push(stat(locale, modifiers.regenerationPerSecond, "stat.regeneration"));
  if (modifiers.luck) result.push(stat(locale, modifiers.luck, "stat.luck"));
  if (modifiers.harvesting) result.push(stat(locale, modifiers.harvesting, "stat.harvesting"));
  if (modifiers.engineering) result.push(stat(locale, modifiers.engineering, "stat.engineering"));
  if (modifiers.knockback) result.push(stat(locale, modifiers.knockback, "stat.knockback"));
  return result;
}

function itemEffectText(
  locale: Locale,
  effect: NonNullable<ItemDefinition["effect"]>,
): string {
  const zh = locale === "zh-CN";
  if (effect.type === "material-on-kill") {
    return zh
      ? `击杀时有 ${Math.round(effect.chance * 100)}% 几率获得 ${effect.amount} 材料`
      : `${Math.round(effect.chance * 100)}% chance to gain ${effect.amount} material on kill`;
  }
  if (effect.type === "heal-on-pickup") return zh ? `消耗品额外恢复 ${effect.amount}` : `Consumables heal +${effect.amount}`;
  if (effect.type === "damage-on-pickup") return zh ? `拾取材料时对随机敌人造成 ${effect.damage} 伤害` : `Picking up material deals ${effect.damage} damage`;
  if (effect.type === "damage-on-enemy-death") return zh ? `敌人死亡时对附近敌人造成 ${effect.damage} 伤害` : `Enemy deaths deal ${effect.damage} nearby damage`;
  if (effect.type === "harvesting-growth") return zh ? `每波收获增长 ${Math.round(effect.percent * 100)}%` : `Harvesting grows ${Math.round(effect.percent * 100)}% each wave`;
  if (effect.type === "free-rerolls") return zh ? `每个升级/商店免费刷新 ${effect.amount} 次` : `${effect.amount} free reroll per upgrade/shop`;
  if (effect.type === "tree-spawns") return zh ? `每批额外生成 ${effect.amount} 棵树` : `Spawns ${effect.amount} extra tree`;
  if (effect.type === "weapon-upgrade-on-shop") return zh ? "进入商店时随机升级一把武器" : "Upgrades a random weapon on shop entry";
  return zh ? `低生命时伤害 +${effect.percent}%` : `+${effect.percent}% damage at low health`;
}

function stat(locale: Locale, value: number, key: MessageKey, percent = false): string {
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${number(locale, value)}${percent ? "%" : ""} ${t(locale, key)}`;
}

function number(locale: Locale, value: number): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
}
